/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * EventStepRegistration — step 3 of 3.
 *
 * Where signups are taken, the two optional things nobody should be forced
 * through, and a summary of what is about to be written.
 *
 * The admin form spends a whole step on sponsors and another on review. Both are
 * folded in here: sponsors and participant notes are collapsed panels, opened
 * only by an organizer who wants them, and the review is a summary card rather
 * than a screen of its own. Publishing is then one click from the place the
 * organizer already is.
 *
 * Fees are set per activity in step 2 — a 5K and a Half under one event are
 * not the same price. What this step asks, and only when at least one of those
 * activities charges, is which currency they are all in: an event settles in
 * one currency, so the question belongs here rather than repeated per activity.
 */

import React, { useState } from 'react';
import OrgInput from './OrgInput';
import OrgTextArea from './OrgTextArea';
import OrgSelect from './OrgSelect';
import OrgDateTime from './OrgDateTime';
import { OrgCardPicker } from './OrgChoice';
import FileUploadInput from '../../common/FileUploadInput';
import { sponsorFileField } from '../../athlohive/pendingUploads';
import { toOptions } from '../../athlohive/lookupOptions';
import {
  sponsorHeadingGroups, levelForHeading, isPreset, parseLevel,
  OTHER, MAX_LABEL_LENGTH, MIN_LEVEL, MAX_LEVEL,
} from '../../athlohive/sponsorHeadings';
import { currencyOptions } from '../../athlohive/currencyCatalog';
import { MAX_PARTICIPANT_NOTE_LENGTH } from '../../athlohive/validateEventForm';
import { formatDateTime } from '../../athlohive/formatDateTime';

const SOURCES = [
  {
    value: 'PLATFORM', icon: '◎', title: 'On AthloHive',
    description: 'We take signups, issue passes and handle check-in.',
  },
  {
    value: 'EXTERNAL', icon: '↗', title: 'On my own site',
    description: 'We link out. You collect the entries yourself.',
  },
];

/**
 * The one-line capacity readout for the review panel.
 *
 * Capacity is set per activity now, so there is no single number to print. An
 * event is only genuinely capped when EVERY activity is — one uncapped category
 * means the event as a whole can still take anybody, and printing the sum of the
 * others would overstate how full it can get. That case says "partly capped"
 * rather than a total, because a total would be a number nobody can act on.
 */
export function summariseCapacity(activities) {
  const list = activities || [];
  if (!list.length) return 'no cap';

  const caps = list.map((activity) => Number(activity.max_participants))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (!caps.length) return 'no cap';
  if (caps.length < list.length) return 'partly capped';

  const total = caps.reduce((sum, value) => sum + value, 0);
  return `${total} place${total === 1 ? '' : 's'}`;
}

/** A collapsed panel for something optional. */
function Fold({ title, note, children, disabled = false }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="orgf-fold">
      <button
        type="button"
        className="orgf-foldhead"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span aria-hidden="true" style={{ color: 'var(--org-accent)' }}>{open ? '−' : '+'}</span>
        <b style={{ fontWeight: 600 }}>{title}</b>
        <span className="orgf-foldnote">{note}</span>
        <span className="orgf-caret" aria-hidden="true">{open ? '⌃' : '⌄'}</span>
      </button>
      {/*
        The fieldset is on the BODY, not the whole fold. Disabling the head too
        would make a locked event's sponsors and participant notes impossible to
        open, and reading them is the only thing left to do with it.
      */}
      {open && (
        <fieldset className="orgf-fieldset orgf-foldbody" disabled={disabled}>
          {children}
        </fieldset>
      )}
    </div>
  );
}

export default function EventStepRegistration({
  // operationCount is deliberately gone: the batch's row count was on the review
  // panel as "Rows written together", which is a fact about how the save is
  // implemented rather than anything an organizer can act on. It still reaches
  // SaveErrorPanel, where "operation 3 of 6" explains what a failure rolled back.
  form, errors, dispatch, lookups,
  // The platform currency catalog. Not part of `lookups` because it is not an
  // ApplicationTable -- see currencyCatalog.js. Defaulted so a caller that has
  // not wired it yet renders an empty picker rather than throwing.
  currencies, currenciesUnavailable = false,
  pendingUploads = {}, setPendingFile, readOnly = false,
}) {
  const set = (field) => (value) => dispatch({ type: 'setField', field, value });

  /*
    Which sponsor rows are writing their own heading.

    This cannot be read off the row alone: a custom heading not yet typed is
    an empty string, which is exactly what "None" stores. Without this the
    organizer picks "Other", nothing appears, and the control looks broken.
    A row whose stored heading is not one of ours — set in Django Admin, or
    typed here earlier — counts as custom without being listed.
  */
  const [customHeadingRows, setCustomHeadingRows] = useState(() => new Set());
  const writesOwnHeading = (sponsor, index) => customHeadingRows.has(index)
    || Boolean(sponsor.sponsor_label && !isPreset(sponsor.sponsor_label));
  const markCustomHeading = (index, on) => setCustomHeadingRows((rows) => {
    const next = new Set(rows);
    if (on) next.add(index); else next.delete(index);
    return next;
  });
  const platform = form.platform_registration || {};
  const external = form.external_registration || {};

  const setPlatform = (field) => (value) => dispatch({
    type: 'updateNested', group: 'platform_registration', field, value,
  });
  const setExternal = (field) => (value) => dispatch({
    type: 'updateNested', group: 'external_registration', field, value,
  });

  // Whether anything on this event actually costs money. The fees live on the
  // activities (step 2), not here — a 5K and a Half under one event are not
  // the same price — so the currency question is asked on their behalf, once,
  // because one event settles in one currency.
  const charges = (form.activities || []).some((activity) => {
    const fee = activity?.fee_amount;
    return fee !== undefined && fee !== null && fee !== '' && Number(fee) > 0;
  });

  return (
    <>
      {/*
        Wrapped here rather than around the whole step: the two Folds below have
        their own heads, which must stay clickable so a locked event can be
        opened and read. This section has no disclosure control in it.
      */}
      <fieldset className="orgf-fieldset" disabled={readOnly}>
      <section className="org-card" style={{ marginBottom: 15 }}>
        <p className="org-lab">How do people register?</p>
        {/*
          This line used to read "Registration is free this phase." It cannot
          say that above a Fee currency field, and it is no longer true: fees
          are set per activity in step 2. It now reports what this event
          actually does rather than what the platform once did.
        */}
        <p style={{ fontSize: 11.5, color: 'var(--org-dim)', margin: '6px 0 14px' }}>
          {charges
            ? 'Some activities charge a fee. Entry is collected on AthloHive.'
            : 'Every activity is free to enter.'}
        </p>

        <OrgCardPicker
          label="Where are signups taken?" required
          value={form.registration_source}
          onChange={set('registration_source')}
          options={SOURCES}
          error={errors.registration_source}
        />

        {form.registration_source === 'PLATFORM' && (
          <div className="orgf-grid" style={{ marginTop: 15 }}>
            <div className="orgf-grid orgf-g3">
              <OrgDateTime
                label="Registration opens" required
                value={platform.registration_start_datetime}
                onChange={setPlatform('registration_start_datetime')}
                error={errors.registration_start_datetime}
              />
              <OrgDateTime
                label="Registration closes" required
                value={platform.registration_end_datetime}
                onChange={setPlatform('registration_end_datetime')}
                error={errors.registration_end_datetime}
              />
            </div>

            <OrgSelect
              label="Registration type" required
              value={form.registration_type_id} onChange={set('registration_type_id')}
              options={toOptions(lookups.registrationTypes)}
              error={errors.registration_type_id}
              placeholder="Choose a type"
            />

            {/*
              Shown only when an activity actually carries a fee — the same
              condition validateEventForm requires it under, so the field
              appears exactly when it is answerable and is never a question
              about money nobody is taking. EXTERNAL registration has no
              currency field at all: the fees are charged on the organizer's
              own site, in whatever currency that site uses.
            */}
            {charges && (
              <OrgSelect
                label="Fee currency" required
                value={platform.currency_id} onChange={setPlatform('currency_id')}
                options={currencyOptions(currencies)}
                error={errors.currency_id}
                placeholder="Choose a currency"
                help={currenciesUnavailable
                  ? 'Currencies could not be loaded. Reload the page, or ask an administrator.'
                  : undefined}
              />
            )}
          </div>
        )}

        {form.registration_source === 'EXTERNAL' && (
          <div className="orgf-grid" style={{ marginTop: 15 }}>
            <OrgInput
              label="Registration URL" required type="url"
              value={external.external_url} onChange={setExternal('external_url')}
              error={errors.external_url}
              placeholder="https://"
              help="Where the Register button sends people."
            />
            <div className="orgf-grid orgf-g2">
              <OrgDateTime
                label="Opens"
                value={external.registration_start_date}
                onChange={setExternal('registration_start_date')}
              />
              <OrgDateTime
                label="Closes"
                value={external.registration_end_date}
                onChange={setExternal('registration_end_date')}
              />
            </div>
            <OrgSelect
              label="Registration type" required
              value={form.registration_type_id} onChange={set('registration_type_id')}
              options={toOptions(lookups.registrationTypes)}
              error={errors.registration_type_id}
              placeholder="Choose a type"
            />
          </div>
        )}
      </section>
      </fieldset>

      <Fold title="Sponsors" note="— optional, add logos and links" disabled={readOnly}>
        {/* Sat above the rows because it was about the set of them, not about
            any one sponsor. validateEventForm no longer raises errors.sponsors
            — several level-1 sponsors are allowed and the strip under the hero
            simply shows the first — so there is nothing left to print here.
        {errors.sponsors && <p className="orgf-err" style={{ margin: '0 0 11px' }}>{errors.sponsors}</p>}
        */}
        {(form.sponsors || []).map((sponsor, index) => (
          <div key={index} className="orgf-manual" style={{ marginTop: 11 }}>
            <div className="orgf-grid orgf-g2">
              <OrgInput
                label={`Sponsor ${index + 1} name`}
                value={sponsor.name}
                onChange={(value) => dispatch({
                  type: 'updateSponsor', index, field: 'name', value,
                })}
              />
              <OrgInput
                label={`Sponsor ${index + 1} website`} type="url"
                value={sponsor.website_url}
                onChange={(value) => dispatch({
                  type: 'updateSponsor', index, field: 'website_url', value,
                })}
              />
            </div>
            {/*
              A fixed list rather than a text box. The event page groups
              sponsors by this heading, so "Gold Sponsors" typed two different
              ways would split one tier into two rows on the public page.
              Picking from a list makes that impossible, and keeps the wording
              consistent across every event on the platform.

              The list is in sponsorHeadings.js, not in the column's options,
              so adding a heading later is one line and no migration.
            */}
            <div className="orgf-grid orgf-g2" style={{ marginTop: 11 }}>
              <OrgSelect
                label={`Sponsor ${index + 1} heading`}
                help="Level 1 sponsors also appear under the hero image."
                /*
                  A heading that is not one of ours — written through "Other",
                  or set in Django Admin — keeps the picker on "Other" so the
                  text box stays open holding it, rather than silently
                  reverting to "None" and losing what was typed.
                */
                value={writesOwnHeading(sponsor, index) ? OTHER : (sponsor.sponsor_label || '')}
                groups={sponsorHeadingGroups()}
                placeholder="None — just the logo"
                onChange={(value) => {
                  if (value === OTHER) {
                    markCustomHeading(index, true);
                    // Keep any custom text already written; only clear a
                    // preset, whose wording the organizer is replacing.
                    if (isPreset(sponsor.sponsor_label)) {
                      dispatch({ type: 'updateSponsor', index, field: 'sponsor_label', value: '' });
                    }
                    return;
                  }
                  markCustomHeading(index, false);
                  dispatch({ type: 'updateSponsor', index, field: 'sponsor_label', value });
                  // The preset carries the level it almost always wants, so
                  // picking "Gold Sponsors" ranks the sponsor without anybody
                  // typing a number. Clearing the heading clears the rank too.
                  dispatch({
                    type: 'updateSponsor', index, field: 'sponsor_level',
                    value: value ? String(levelForHeading(value) ?? '') : '',
                  });
                }}
              />
            </div>
            {/*
              Only "Other" exposes the raw pair. A preset has already set both,
              and showing a level box beside every sponsor invites fiddling
              with numbers that the list had right.
            */}
            {writesOwnHeading(sponsor, index) && (
              <div className="orgf-grid orgf-g2" style={{ marginTop: 11 }}>
                <OrgInput
                  label={`Sponsor ${index + 1} heading text`}
                  help={`Up to ${MAX_LABEL_LENGTH} characters. Printed exactly as typed.`}
                  value={sponsor.sponsor_label || ''}
                  maxLength={MAX_LABEL_LENGTH}
                  onChange={(value) => dispatch({
                    type: 'updateSponsor', index, field: 'sponsor_label',
                    value: String(value).slice(0, MAX_LABEL_LENGTH),
                  })}
                />
                <OrgInput
                  label={`Sponsor ${index + 1} level`} type="number"
                  help={`${MIN_LEVEL} to ${MAX_LEVEL}. Level 1 shows under the hero image.`}
                  value={sponsor.sponsor_level || ''}
                  error={
                    sponsor.sponsor_level && parseLevel(sponsor.sponsor_level) === null
                      ? `Enter a whole number from ${MIN_LEVEL} to ${MAX_LEVEL}.`
                      : undefined
                  }
                  onChange={(value) => dispatch({
                    type: 'updateSponsor', index, field: 'sponsor_level', value,
                  })}
                />
              </div>
            )}
            <div className="orgf-field" style={{ marginTop: 11 }}>
              <label htmlFor={`orgf-sponsor-logo-${index}`}>Sponsor {index + 1} logo</label>
              {/*
                Same deferred-upload contract as the cover image. Without the
                pending pair the picked logo went nowhere — and because a
                sponsor logo is optional, it failed silently: the organizer
                added a sponsor, believed the logo was attached, and the saved
                row simply had none.
              */}
              <FileUploadInput
                is_public
                value={sponsor.logo_url}
                onChange={(value) => dispatch({
                  type: 'updateSponsor', index, field: 'logo_url', value,
                })}
                pendingFile={pendingUploads[sponsorFileField(index, 'logo_url')] || null}
                onPendingFile={(file) => setPendingFile(sponsorFileField(index, 'logo_url'), file)}
                allowedFileTypes={['image']}
                inputProps={{
                  id: `orgf-sponsor-logo-${index}`,
                  name: sponsorFileField(index, 'logo_url'),
                }}
              />
            </div>
            <p style={{ margin: '11px 0 0' }}>
              <button
                type="button"
                className="orgf-remove"
                onClick={() => dispatch({ type: 'removeSponsor', index })}
              >
                Remove sponsor {index + 1}
              </button>
            </p>
          </div>
        ))}
        <button
          type="button"
          className="orgf-add"
          style={{ marginTop: 11 }}
          onClick={() => dispatch({ type: 'addSponsor' })}
        >
          + Add a sponsor
        </button>
      </Fold>

      <Fold title="Prerequisites & preparation" note="— optional notes for participants" disabled={readOnly}>
        <div className="orgf-grid" style={{ marginTop: 13 }}>
          <OrgTextArea
            label="Prerequisites"
            value={form.prerequisites} onChange={set('prerequisites')}
            error={errors.prerequisites}
            maxLength={MAX_PARTICIPANT_NOTE_LENGTH}
            placeholder="Anything someone must have done or must bring."
          />
          <OrgTextArea
            label="Preparation steps"
            value={form.preparation_steps} onChange={set('preparation_steps')}
            error={errors.preparation_steps}
            maxLength={MAX_PARTICIPANT_NOTE_LENGTH}
            placeholder="How to get ready in the weeks before."
          />
        </div>
      </Fold>

      <section className="org-card">
        <p className="org-lab">Ready to publish</p>
        <p style={{ fontSize: 11.5, color: 'var(--org-dim)', margin: '6px 0 12px', lineHeight: 1.55 }}>
          Everything below is written in one go. If any part of it fails, nothing is saved and you
          land back here with the reason.
        </p>

        <div className="orgf-sum"><span>Event</span><b>{form.title || '—'}</b></div>
        <div className="orgf-sum">
          <span>When</span>
          <b>
            {/*
              No timezone argument, deliberately. `formatDateTime(value, zone)`
              treats its value as a STORED UTC instant and moves it into that
              zone — right for the event list, whose values come from the
              database. This one comes from the form, where it is already a wall
              clock in the event's own zone. Passing the zone shifted it a second
              time: 07:00 entered in Asia/Kolkata displayed as 12:30 pm, right
              above the Publish button.

              The zone is named alongside instead, so the time is unambiguous
              without being re-converted.
            */}
            {form.start_datetime ? formatDateTime(form.start_datetime) : '—'}
            {form.start_datetime && form.timezone && (
              <span style={{ color: 'var(--org-dim)', fontWeight: 400 }}>
                {' '}{form.timezone}
              </span>
            )}
          </b>
        </div>
        <div className="orgf-sum"><span>Where</span><b>{form.location || '—'}</b></div>
        <div className="orgf-sum">
          <span>Activities</span><b>{(form.activities || []).length}</b>
        </div>
        <div className="orgf-sum">
          <span>Registration</span>
          <b>
            {form.registration_source === 'EXTERNAL' ? 'Your own site' : 'AthloHive'}
            {form.registration_source === 'PLATFORM' && ` · ${summariseCapacity(form.activities)}`}
          </b>
        </div>
        <div className="orgf-sum">
          <span>Sponsors</span><b>{(form.sponsors || []).length}</b>
        </div>
      </section>
    </>
  );
}
